import React, { useState, useLayoutEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Chip,
  Tabs,
  Tab,
  Select,
  SelectItem
} from '@heroui/react';
import { useAuthStore } from '../../stores/auth.store';
import type { UsersPermissionsUser } from '../../types/user.model';
import ApiClient from '../../services/api/axios/apiClient';
import InvitationList from '../empresa/InvitationList';
import ConfigurationPaymentsAndSubscriptions from '../paymentSubscriptions/ConfigurationPaymentsAndSubscriptions';
import CompanyConfigurationTab from '../empresa/CompanyConfigurationTab';

interface UserConfigModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const UserConfigModal = ({ isOpen, onOpenChange }: UserConfigModalProps) => {
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState<Partial<UsersPermissionsUser>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userDocumentId, setUserDocumentId] = useState<number>(0);  

  useLayoutEffect(() => {
    const fetchDatosUsuario = async () => {
      if (user) {
        // Set user data first
        setFormData(prev => ({
          ...prev,
          username: user.username,
          email: user.email,
        }));

        try {
          const response = await ApiClient.get(`/users/${user.id}`);
          if (response.data) {
            const datos = response.data;
            setUserDocumentId(datos.id);
            const attributes = datos;
            setFormData(prev => ({
              ...prev,
              nombre_completo: attributes.nombre_completo,
              telefono: attributes.telefono,
              tipoidentificacion: attributes.tipoidentificacion,
              identificacion: attributes.identificacion,
            }));
          }
        } catch (error) {
          console.error('Error fetching datos usuario:', error);
        }
      }
    };

    if (isOpen && activeTab === 'profile') {
      fetchDatosUsuario();
    }
  }, [user, isOpen, activeTab]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (keys: any) => {
    const value = keys.currentKey;
    setFormData(prev => ({
      ...prev,
      tipoidentificacion: value as string
    }));
    if (errors.tipoidentificacion) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tipoidentificacion;
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.nombre_completo || formData.nombre_completo.trim().length < 3) {
      newErrors.nombre_completo = 'El nombre completo debe tener al menos 3 caracteres';
    }
    
    if (!formData.telefono || !/^\d+$/.test(formData.telefono)) {
      newErrors.telefono = 'Por favor ingrese un número de teléfono válido';
    }

    if (!formData.tipoidentificacion) {
      newErrors.tipoidentificacion = 'Por favor seleccione un tipo de identificación';
    }

    if (!formData.identificacion || formData.identificacion.trim().length < 5) {
      newErrors.identificacion = 'La identificación debe tener al menos 5 caracteres';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const payload = {
      nombre_completo: formData.nombre_completo,
      telefono: formData.telefono,
      tipoidentificacion: formData.tipoidentificacion,
      identificacion: formData.identificacion,
    };

    try {

        const result = await ApiClient.put(`/users/${userDocumentId}`, payload);
        const dataAnterior = user?.empresa;

        updateUser({...result.data, empresa:dataAnterior})
      
      // Optionally, you can refetch the user data or update the store
      // For now, just close the modal
    } catch (error: any) {
      console.error('Error saving user data:', error);
      setErrors({ submit: 'Error al guardar los datos. Por favor intente nuevamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onOpenChange}
      placement="center"
      size="full"
      scrollBehavior="inside"
    >
      <ModalContent className="">
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              Configuración de la Cuenta
            </ModalHeader>
            <ModalBody className="flex-1">
              <Tabs 
                aria-label="Configuración de usuario" 
                selectedKey={activeTab}
                onSelectionChange={(key) => setActiveTab(key as string)}
                className="w-full"
              >
                <Tab key="profile" title="Configuración de Perfil">
                  <form id="user-config-form" onSubmit={handleSubmit} className="py-4">
                    <div className="space-y-4">
                      <Input
                        label="Nombre de Usuario"
                        name="username"
                        value={formData.username || ''}
                        variant="bordered"
                        isRequired
                        isReadOnly
                      />
                      
                      <Input
                        label="Nombre Completo"
                        name="nombre_completo"
                        value={formData.nombre_completo || ''}
                        onChange={handleChange}
                        variant="bordered"
                        isInvalid={!!errors.nombre_completo}
                        errorMessage={errors.nombre_completo}
                      />

                      <Input
                        label="Teléfono"
                        name="telefono"
                        value={formData.telefono || ''}
                        onChange={handleChange}
                        variant="bordered"
                        isInvalid={!!errors.telefono}
                        errorMessage={errors.telefono}
                      />

                      <div className="flex gap-4">
                        <div className="w-1/3">
                          <Select
                            label="Tipo de Identificación"
                            name="tipoidentificacion"
                            defaultSelectedKeys={[user?.tipoidentificacion as string]}
                            onSelectionChange={handleSelectChange}
                            isInvalid={!!errors.tipoIdentificacion}
                            errorMessage={errors.tipoIdentificacion}
                          >
                            <SelectItem key="CC">CC</SelectItem>
                            <SelectItem key="CE">CE</SelectItem>
                            <SelectItem key="NIT">NIT</SelectItem>
                          </Select>
                        </div>
                        <div className="w-2/3">
                          <Input
                            label="Identificación"
                            name="identificacion"
                            value={formData.identificacion || ''}
                            onChange={handleChange}
                            variant="bordered"
                            isInvalid={!!errors.identificacion}
                            errorMessage={errors.identificacion}
                          />
                        </div>
                      </div>
                      
                      <Input
                        label="Correo Electrónico"
                        name="email"
                        type="email"
                        value={formData.email || ''}
                        variant="bordered"
                        isRequired
                        isReadOnly
                      />
                      
                      <div className="pt-2">
                        <p className="text-sm text-gray-500">Información del Rol</p>
                        <div className="mt-1">
                          <Chip 
                            color="primary" 
                            variant="flat"
                          >
                            {user?.role?.name || 'N/A'}
                          </Chip>
                        </div>
                      </div>
                    </div>
                    
                    {errors.submit && (
                      <div className="mt-4 p-3 bg-danger/20 text-danger text-sm rounded-md">
                        {errors.submit}
                      </div>
                    )}
                  </form>
                </Tab>
                <Tab key="company-users" title="Usuarios de la Empresa">
                  <div className="py-4">
                    {user?.empresa ? (
                      <InvitationList />
                    ) : (
                      <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-500">No se ha encontrado información de la empresa asociada.</p>
                      </div>
                    )}
                  </div>
                </Tab>
                <Tab key="payments-subscriptions" title="Pagos y Suscripciones">
                  <div className="py-4">
                    {user ? (
                      <ConfigurationPaymentsAndSubscriptions />
                    ) : (
                      <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-500">No se ha encontrado información de usuario para mostrar pagos y suscripciones.</p>
                      </div>
                    )}
                  </div>
                </Tab>
                <Tab key="company-info" title="Información de la Empresa">
                  <div className="py-4">
                    <CompanyConfigurationTab />
                  </div>
                </Tab>
              </Tabs>
            </ModalBody>
            <ModalFooter>
              <Button color="danger" variant="flat" onPress={handleClose}>
                Cerrar
              </Button>
              {activeTab === 'profile' && (
                <Button 
                  color="primary" 
                  type="submit" 
                  form="user-config-form"
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  Guardar Cambios
                </Button>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default UserConfigModal;